import {useContext, useState} from 'react';
import {UpdatePassenger, UpdatePassengerAndFlight} from '../../graphql';
import {useApolloClient} from 'react-apollo';
import {PaxNodeInput} from '__generated__/graphql/types';
import {PaxNodeFields} from '../types';
import {produce} from "immer";
import {computeLegWeightsAndCounts} from "common/flight-leg/util";
import {OrgDataContext} from "context/orgData";
import {MutationOptions} from 'apollo-client';
import {FetchResult} from "apollo-link";

interface ReturnValue {
    sendUpdate: (paxNodeInput: PaxNodeInput) => Promise<PaxNodeFields>,
    isProcessing: boolean
}

function useSubmit(currentPaxNode: PaxNodeFields, options?: {
    onProcessingStatusChanged?: (isProcessing: boolean) => void
}): ReturnValue {
    const orgData = useContext(OrgDataContext);
    const apolloClient = useApolloClient();

    const [ isProcessing, setIsProcessing ] = useState(false);

    type MutationResult = FetchResult<{ setPaxNode: PaxNodeFields }>

    async function submitUpdate(paxNodeInput: PaxNodeInput) {
        // Re-compute leg weights and counts
        // Find passenger is leg lists and update pax weights.
        // Then, re-calculate weights of each leg that was changed.

        let mutationOptions: MutationOptions;

        if (currentPaxNode.currentCarrierDoc && 'legsArray' in currentPaxNode.currentCarrierDoc){
            let flightNeedsUpdate = false;

            let legs = currentPaxNode.currentCarrierDoc?.legsArray || [];
            legs = legs.map((leg) => produce(leg, (draft) => {

                let legNeedsUpdate = false;

                // Update passengers
                draft.paxIDs.forEach((pax) => {
                    if (pax._id === paxNodeInput._id) {
                        legNeedsUpdate = true;
                        pax.paxWeight = paxNodeInput.paxWeight;
                        pax.bagWeight = paxNodeInput.bagWeight;
                        pax.bagCount = paxNodeInput.bagCount;
                    }
                    return pax;
                })

                if (!legNeedsUpdate) return;

                flightNeedsUpdate = true;

                // Recalculate weights and counts
                const newWeights = computeLegWeightsAndCounts(
                    draft.paxIDs, draft.cgoIDs
                )

                // Assign new weight/count fields
                Object.assign(draft, newWeights);
            }))

            if (flightNeedsUpdate){
                const resultLegs = legs.map((leg) => {
                    return {
                        ...leg,
                        paxIDs: leg.paxIDs.map((e) => e._id),
                        cgoIDs: leg.cgoIDs.map((e) => e._id)
                    }
                })

                const serializedLegs = JSON.stringify(Object.assign({}, resultLegs));

                const flightPayload = {
                    _id: currentPaxNode.currentCarrierDoc._id,
                    tpID: currentPaxNode.currentCarrierDoc.tpID?._id || orgData.getOrgIDByType('transporter'),
                    customerID: currentPaxNode.currentCarrierDoc.customerID?._id || orgData.getOrgIDByType('customer'),
                    legs: serializedLegs
                }

                mutationOptions = {
                    mutation: UpdatePassengerAndFlight,
                    variables: {
                        paxPayload: paxNodeInput,
                        flightPayload: flightPayload,
                        orgID: orgData.organization?._id
                    }
                };
            }
        }
        else {
            mutationOptions = {
                mutation: UpdatePassenger,
                variables: {
                    paxPayload: paxNodeInput
                }
            };
        }

        return await apolloClient.mutate(mutationOptions) as MutationResult;
    }

    async function sendUpdate(paxNodeInput: PaxNodeInput){
        setIsProcessing(true);

        let result: MutationResult;
        try {
            await submitUpdate(paxNodeInput);
        }
        catch (err){
            setIsProcessing(false);
            throw err;
        }

        setIsProcessing(false);

        return result?.data?.setPaxNode;
    }

    return { sendUpdate, isProcessing };
}

export default useSubmit;